import { DEFAULT_TIME_ZONE, formatJpy, isValidTimeZone, wallClockInZone } from '@saas/shared';

export { formatJpy };

type DateLike = Date | string | number | null | undefined;

/**
 * 表示のタイムゾーン（02 §11 #4「保存は UTC・表示はテナントの TZ」・03 §20.4「1 テナント 1 つ」）。
 *
 * 端末のローカル TZ は使わない（海外出張・端末の設定違いで同じ行が人によって違う日付に見えるのを防ぐ）。
 * シェル（`apps/web/src/app/**`）がログイン直後と `me` の更新時に
 *
 *     setDisplayTimeZone(tenantTimeZone(me.tenant.settings))   // @saas/shared の tenantTimeZone
 *
 * を呼ぶ。呼ばれるまでの既定は `Asia/Tokyo`（`DEFAULT_TIME_ZONE`）。
 * 値はモジュール変数で持つ（React の外からも `formatDate()` を呼ぶため。依存は増やさない）。
 */
let displayTimeZone: string = DEFAULT_TIME_ZONE;

/** 表示に使うタイムゾーンを差し替える。不正な名前は無視して既定のまま（画面を壊さない） */
export function setDisplayTimeZone(tz: string | null | undefined): string {
  displayTimeZone = isValidTimeZone(tz) ? tz : DEFAULT_TIME_ZONE;
  return displayTimeZone;
}

/** いま表示に使っているタイムゾーン */
export function getDisplayTimeZone(): string {
  return displayTimeZone;
}

function toDate(v: DateLike): Date | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, '0');

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 西暦 YYYY-MM-DD（05 §3.3c）。
 * 暦日の文字列（'2026-09-17'）は「その日」そのものなので変換しない。日時はテナント TZ の暦日にする
 */
export function formatDate(v: DateLike): string {
  if (typeof v === 'string' && ISO_DATE.test(v)) return v;
  const d = toDate(v);
  if (!d) return '—';
  const w = wallClockInZone(d, displayTimeZone);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
}

/** YYYY-MM-DD HH:mm（テナント TZ）。暦日の文字列はその日の 0 時として扱う（変換しない） */
export function formatDateTime(v: DateLike): string {
  if (typeof v === 'string' && ISO_DATE.test(v)) return `${v} 00:00`;
  const d = toDate(v);
  if (!d) return '—';
  const w = wallClockInZone(d, displayTimeZone);
  return `${w.year}-${pad(w.month)}-${pad(w.day)} ${pad(w.hour)}:${pad(w.minute)}`;
}

/** HH:mm（テナント TZ） */
export function formatTime(v: DateLike): string {
  const d = toDate(v);
  if (!d) return '—';
  const w = wallClockInZone(d, displayTimeZone);
  return `${pad(w.hour)}:${pad(w.minute)}`;
}

/** 件数バッジ。99 を超えたら「99+」（05 §7.11-6） */
export function formatCount(n: number, max = 99): string {
  return n > max ? `${max}+` : String(n);
}

/**
 * 金額の表示。負数は ▲（U+25B2）で示し、色は使わない（05 §7.11-4）。
 * null は「—」。
 */
export function formatMoney(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const s = formatJpy(v);
  return s.startsWith('-') ? `▲${s.slice(1)}` : s;
}

/** 今日（テナント TZ）の YYYY-MM-DD */
export function todayLocalIso(now = new Date()): string {
  return formatDate(now);
}

/** 暦日どうしの差（to - from）。両方 YYYY-MM-DD */
export function diffCalendarDays(from: string, to: string): number {
  const a = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const b = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.round((b - a) / 86_400_000);
}

/** 数値の 3 桁区切り（件数など） */
export function formatNumber(n: number | string): string {
  const num = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(num)) return String(n);
  return num.toLocaleString('ja-JP');
}

export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
